"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Check, Copy } from "lucide-react";

type DocCodeBlockProps = {
  language?: string;
  children: string;
};

function getText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  return "";
}

export function DocCodeBlock({ language, children }: DocCodeBlockProps) {
  const t = useTranslations("docs.nav");
  const [copied, setCopied] = useState(false);
  const text = getText(children);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable, silently no-op
    }
  };

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-xl border bg-muted px-5 py-4 text-sm leading-relaxed">
        <code className="font-mono text-foreground">{children}</code>
      </pre>
      {language && (
        <span className="absolute top-2 inset-s-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {language}
        </span>
      )}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={t("copyCode")}
        className="absolute top-2 inset-e-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
